import React, { useRef } from 'react';
import { AnimatedGradientBorder, SelectionIndicator } from 'components';
import withDefaultProps from 'helpers/withDefaultProps';
import { useLongPress } from 'hooks';
import PropTypes from 'prop-types';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import styled, { css } from 'styled-components';
import { media } from 'utils/rwd';
import RequestPendingSpinner from 'components/RequestPendingSpinner/RequestPendingSpinner';
import ImageWithPlaceholder from 'components/ImageWithPlaceholder/ImageWithPlaceholder';
import MoreOptionsComponent from './MoreOptionsComponent';

export const Wrapper = styled.div`
  flex: 0 1 100%;
  min-width: 210px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  position: relative;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  cursor: pointer;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    ${({ $selectionMode }) =>
      !$selectionMode &&
      css`
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      `}
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px rgba(190, 22, 34, 0.28);
  }
  ${media.xs`
    flex: 0 1 calc(50% - 10px);
  `}

  ${media.sm`
    flex: 0 1 calc(50% - 10px);
  `}

  ${media.md`
    flex: 0 1 calc(33.333% - 10px);
  `}

  ${media.lg`
    flex: 0 1 calc(20% - 10px);
  `}
`;

export const ImageWrapper = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  padding: 0;
  border: none;
  overflow: hidden;
  position: relative;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  &:hover {
    img,
    video {
      transform: ${({ $selectionMode }) => ($selectionMode ? 'none' : 'scale(1.1)')};
    }
  }
`;

const SelectionTint = styled.div`
  position: absolute;
  inset: 0;
  z-index: 20;
  pointer-events: none;
  background-color: ${({ $selected }) =>
    $selected ? 'rgba(190, 22, 34, 0.2)' : 'rgba(190, 22, 34, 0)'};
  transition: background-color 180ms cubic-bezier(0.2, 0, 0, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const Image = styled(ImageWithPlaceholder)`
  position: absolute;
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  object-position: center;
  transition: transform 0.3s ease;
  will-change: transform;
`;

const Video = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  transition: transform 0.3s ease;
  will-change: transform;
`;

const LabelsWrapper = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  gap: 4px;
  z-index: 999;
`;

const Label = styled.div`
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 2px 6px;
  font-size: 0.7em;
  border-radius: 4px;
  z-index: 9999;
`;

export const InfoWrapper = styled.div`
  padding: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: black;
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  background-color: white;
`;

export const Title = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

function ItemCard({
  title,
  mediaUrl,
  mediaType,
  onClick,
  isDeleting,
  options,
  labels,
  highlighted = false,
  highlightColors = ['#f82fc6', '#5a4ff9', '#ff923e'],
  selectionMode = false,
  selected = false,
  onSelectionToggle,
  onLongPress,
  children,
  ...props
}) {
  const videoRef = useRef(null);
  const longPressHandlers = useLongPress(onLongPress, {
    enabled: Boolean(onLongPress) && !selectionMode,
  });

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const handleSelectionKeyDown = (event) => {
    if (!selectionMode || !['Enter', ' '].includes(event.key)) return;

    event.preventDefault();
    onSelectionToggle?.();
  };

  const handleCardClick = () => {
    if (selectionMode) onSelectionToggle?.();
  };

  return (
    <Wrapper
      {...props}
      {...longPressHandlers}
      $selectionMode={selectionMode}
      onClick={handleCardClick}
      onKeyDown={handleSelectionKeyDown}
      role={selectionMode ? 'checkbox' : undefined}
      aria-checked={selectionMode ? selected : undefined}
      aria-label={selectionMode ? title : undefined}
      tabIndex={selectionMode ? 0 : undefined}
    >
      <RequestPendingSpinner color="#be1622" isActive={isDeleting} />
      <AnimatedGradientBorder colors={highlightColors} disabled={!highlighted}>
        <ImageWrapper
          onClick={selectionMode ? undefined : onClick}
          onMouseEnter={selectionMode ? undefined : handleMouseEnter}
          onMouseLeave={selectionMode ? undefined : handleMouseLeave}
          $clickable={!selectionMode && !!onClick}
          $selectionMode={selectionMode}
        >
          {!selectionMode && options && <MoreOptionsComponent options={options} />}
          {mediaType === 'image' ? (
            <Image src={mediaUrl} alt="Card image" />
          ) : (
            <Video ref={videoRef} src={mediaUrl} muted loop playsInline preload="metadata" />
          )}
          {!selectionMode && labels && (
            <LabelsWrapper>
              {labels.map((label) => (
                <Label key={label}>{label}</Label>
              ))}
            </LabelsWrapper>
          )}
        </ImageWrapper>
        <InfoWrapper>
          <Title>{title}</Title>
          {children}
        </InfoWrapper>
      </AnimatedGradientBorder>
      {selectionMode && (
        <>
          <SelectionTint $selected={selected} />
          <SelectionIndicator selected={selected} />
        </>
      )}
    </Wrapper>
  );
}

ItemCard.propTypes = {
  title: PropTypes.string.isRequired,
  mediaUrl: PropTypes.string.isRequired,
  mediaType: PropTypes.oneOf(['image', 'video']).isRequired,
  onClick: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
    }),
  ),
  labels: PropTypes.arrayOf(PropTypes.string),
  selectionMode: PropTypes.bool,
  selected: PropTypes.bool,
  onSelectionToggle: PropTypes.func,
  onLongPress: PropTypes.func,
  ...withDefaultProps(),
};
export const ItemCardSkeleton = () => (
  <Wrapper>
    <ImageWrapper>
      <Skeleton
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          aspectRatio: '1/1',
        }}
        borderRadius={0}
      />
    </ImageWrapper>
    <InfoWrapper>
      <Skeleton width={120} />
    </InfoWrapper>
  </Wrapper>
);

export default ItemCard;
